import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Button, Chip, Checkbox, IconButton, Menu, MenuItem,
  Dialog, Slide, Typography
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { fetchData } from "../../BaseUrl";
import DynamicTable from "../../DynamicComponent/DynamicTable";
import ComplaintForm from "./ComplaintForm";
import Toast from "../../DynamicComponent/Toast";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const generateFallback = () => {
  const categories = ["Discipline", "Academic Issue", "Classroom Misbehaviour"];
  const statuses = ["Resolved", "Under Investigation", "Inprogress"];
  const names = ["Aarav Kumar","Priya Sharma","Karthik Raj","Divya Nair","Sanjay Patel","Meera Das","Rohan Singh","Kavitha Menon","Arjun Iyer","Nisha Verma","Deepak Mishra","Anita Pillai","Suresh Rajan","Lakshmi Mohan","Praveen Kumar"];
  return names.map((name, i) => ({
    id: null,
    complaintId: `CMP${String(i + 1).padStart(3, "0")}`,
    studentName: name,
    category: categories[i % 3],
    title: `${categories[i % 3]} - Issue ${i + 1}`,
    date: `2025-0${(i % 9) + 1}-${String((i % 28) + 1).padStart(2, "0")}`,
    status: statuses[i % 3],
  }));
};

const statusStyle = (status) => {
  if (status === "Resolved") return { backgroundColor: "#e8f5e9", color: "#4caf50" };
  if (status === "Under Investigation") return { backgroundColor: "#ffebee", color: "#f44336" };
  return { backgroundColor: "#e3f2fd", color: "#1976d2" };
};

const actionBtnStyle = {
  border: "1px solid #D5D7DA",
  borderRadius: "8px",
  color: "#8F95B2",
  textTransform: "none",
  fontSize: "13px",
  padding: "6px 14px",
};

function ActionMenu({ row, onView, onDelete }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}><MoreVertIcon fontSize="small" /></IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => { setAnchorEl(null); onView(row); }}>View</MenuItem>
        <MenuItem onClick={() => { setAnchorEl(null); onDelete(row); }} sx={{ color: "#f44336" }}>Delete</MenuItem>
      </Menu>
    </>
  );
}

// Simple view dialog for complaint details
function ViewComplaintDialog({ complaint, onClose }) {
  if (!complaint) return null;
  const fields = [
    ["Complaint ID", complaint.complaintId],
    ["Student / Complainant", complaint.studentName || complaint.complaintName || complaint.name],
    ["Category", complaint.category],
    ["Title / Subject", complaint.title || complaint.subject],
    ["Date", complaint.date],
    ["Status", complaint.status],
    ["Email", complaint.email],
    ["Contact", complaint.contactNo],
    ["Description", complaint.description],
  ].filter(([, v]) => v);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography sx={{ fontWeight: "bold", fontSize: "16px" }}>Complaint Details</Typography>
        <Button onClick={onClose} size="small" sx={{ color: "#666", textTransform: "none" }}>Close</Button>
      </Box>
      {fields.map(([label, value]) => (
        <Box key={label} sx={{ display: "flex", gap: 1, mb: 1, borderBottom: "1px solid #f0f0f0", pb: 1 }}>
          <Typography sx={{ fontWeight: "bold", fontSize: "13px", color: "#555", minWidth: 150 }}>{label}:</Typography>
          <Typography sx={{ fontSize: "13px", color: "#333" }}>{value}</Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function ComplaintManagement() {
  const [rawData, setRawData] = useState([]);
  const [rows, setRows] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const handleView = useCallback((row) => setViewRow(row), []);
  const handleDeleteRequest = useCallback((row) => setDeleteRow(row), []);

  const handleDeleteConfirm = () => {
    // Complaints are managed by admin; teacher can only remove from local view
    setRawData((prev) => prev.filter((r) => r !== deleteRow));
    setDeleteRow(null);
    setToast({ open: true, severity: "success", message: "Complaint removed from view" });
  };

  const buildRows = useCallback((data, onView, onDelete) => {
    return data.map((r) => ({
      checkbox: <Checkbox size="small" />,
      complaintId: r.complaintId || r.id,
      studentName: r.studentName || r.complaintName || r.name,
      category: r.category,
      title: r.title || r.subject,
      date: r.date ? r.date.substring(0, 10) : "",
      statusChip: (
        <Chip label={r.status} size="small" sx={{ ...statusStyle(r.status), fontWeight: "bold", fontSize: "12px", borderRadius: "20px" }} />
      ),
      action: <ActionMenu row={r} onView={onView} onDelete={onDelete} />,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = useCallback(async () => {
    try {
      const response = await fetchData("admin/complaint/list");
      const data = response?.data && Array.isArray(response.data) ? response.data : generateFallback();
      setRawData(data);
      setRows(buildRows(data, handleView, handleDeleteRequest));
    } catch (_) {
      const data = generateFallback();
      setRawData(data);
      setRows(buildRows(data, handleView, handleDeleteRequest));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildRows, handleView, handleDeleteRequest]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (rawData.length >= 0) setRows(buildRows(rawData, handleView, handleDeleteRequest));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawData]);

  const columns = [
    { label: "", field: "checkbox" },
    { label: "Complaint Id", field: "complaintId" },
    { label: "Student Name", field: "studentName" },
    { label: "Complaint Category", field: "category" },
    { label: "Complaint Title", field: "title" },
    { label: "Complaint Date", field: "date" },
    { label: "Status", field: "statusChip" },
    { label: "Action", field: "action" },
  ];

  return (
    <Box sx={{ padding: "1rem", textAlign: "left" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />
      <p style={{ color: "rgba(0,0,0,0.5)", fontWeight: "bold", fontSize: "14px", margin: 0 }}>
        Pages / Complaint Management
      </p>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1, mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <h2 style={{ margin: 0 }}>Complaint Management</h2>
          <Chip label={`${rawData.length} Records`} size="small" sx={{ backgroundColor: "#e8f5e9", color: "#4caf50", fontWeight: "bold", fontSize: "12px", borderRadius: "20px", padding: "4px 12px" }} />
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button startIcon={<DeleteOutlineIcon />} sx={actionBtnStyle}>Delete</Button>
          <Button startIcon={<FilterListIcon />} sx={actionBtnStyle}>Filters</Button>
          <Button startIcon={<FileDownloadIcon />} sx={actionBtnStyle}>Export</Button>
          <Button startIcon={<AddIcon />} onClick={() => setFormOpen(true)} sx={{ background: "linear-gradient(90deg,#4A7BA8,#244C77)", color: "#fff", textTransform: "none", borderRadius: "8px", padding: "8px 20px", fontSize: "13px" }}>
            ADD
          </Button>
        </Box>
      </Box>
      <DynamicTable columns={columns} rows={rows} />

      {/* Create Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} TransitionComponent={Transition} fullWidth maxWidth="sm">
        <ComplaintForm handleClose={() => { setFormOpen(false); loadData(); }} />
      </Dialog>

      {/* View Dialog */}
      <Dialog open={Boolean(viewRow)} onClose={() => setViewRow(null)} fullWidth maxWidth="sm">
        <ViewComplaintDialog complaint={viewRow} onClose={() => setViewRow(null)} />
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={Boolean(deleteRow)} onClose={() => setDeleteRow(null)} maxWidth="xs" fullWidth>
        <Box sx={{ p: 3 }}>
          <Typography sx={{ fontWeight: "bold", fontSize: "16px", mb: 1 }}>Remove Complaint</Typography>
          <Typography sx={{ color: "#666", fontSize: "14px", mb: 3 }}>
            Remove this complaint from your view?
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={() => setDeleteRow(null)} sx={{ color: "#666", textTransform: "none", border: "1px solid #D5D7DA", borderRadius: "8px", px: 3 }}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} sx={{ backgroundColor: "#f44336", color: "#fff", textTransform: "none", borderRadius: "8px", px: 3, "&:hover": { backgroundColor: "#d32f2f" } }}>Remove</Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}
